/**
 * Company Enrichment Service
 * Uses AI to gather background information about companies
 */

import { Company } from '../types';
import { openaiService } from './openaiService';

// Enrichment data structure
export interface CompanyEnrichmentData {
  description?: string;
  industry?: string;
  founded?: string;
  headquarters?: string;
  website?: string;
  employees?: string;
  revenue?: string;
  products?: string[];
  competitors?: string[];
  keyPeople?: {
    name: string;
    position: string;
  }[];
  recentNews?: {
    title: string;
    date: string;
    summary: string;
  }[];
  socialMedia?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
  };
  businessModel?: string;
  targetMarket?: string;
  fundingInfo?: string;
  companySize?: 'startup' | 'small' | 'medium' | 'large' | 'enterprise';
  tags?: string[];
  lastEnriched?: Date;
  confidence?: 'high' | 'medium' | 'low';
}

class CompanyEnrichmentService {
  private cache: Map<string, { data: CompanyEnrichmentData; timestamp: number }> = new Map();
  private cacheTimeout = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Enrich company data using AI-powered research
   */
  async enrichCompany(company: Company | { name: string; website?: string }): Promise<CompanyEnrichmentData> {
    const companyName = company.name;
    
    // Check cache
    const cached = this.cache.get(companyName);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    try {
      // Build research query
      const researchQuery = this.buildResearchQuery(company);
      
      // Use OpenAI to gather information
      const enrichmentData = await this.performResearch(researchQuery);
      
      // Cache the result
      this.cache.set(companyName, {
        data: enrichmentData,
        timestamp: Date.now()
      });

      return enrichmentData;
    } catch (error) {
      console.error('Error enriching company data:', error);
      return {
        confidence: 'low',
        lastEnriched: new Date()
      };
    }
  }

  /**
   * Build a comprehensive research query
   */
  private buildResearchQuery(company: Company | { name: string; website?: string }): string {
    let query = `Research the company "${company.name}"`;
    
    if (company.website) {
      query += ` (website: ${company.website})`;
    }
    
    query += ` and provide the following information:
    1. Company description and mission
    2. Industry and market segment
    3. Founded year and headquarters location
    4. Number of employees and revenue (if available)
    5. Main products or services
    6. Key competitors
    7. Recent news or developments
    8. Key executives or founders
    9. Business model and target market
    10. Funding information (if applicable)
    
    Format the response as structured data.`;
    
    return query;
  }

  /**
   * Perform AI-powered research
   */
  private async performResearch(query: string): Promise<CompanyEnrichmentData> {
    try {
      // Use OpenAI with enhanced prompt for company research
      const systemPrompt = `You are an expert business analyst AI that researches companies. 
      When given a company name, provide comprehensive, accurate information based on your knowledge up to your training date.
      If you don't have certain information, say "Not available" for that field.
      
      Try to provide information in a structured format that includes:
      - Company description and overview
      - Industry and market segment
      - Founded year and headquarters
      - Employee count (estimate if needed)
      - Main products or services
      - Key competitors
      - Business model
      - Target market
      - Any notable information
      
      Be factual and concise. Format your response as clearly structured text or JSON if possible.`;

      const response = await openaiService.generalQuery(query, systemPrompt);

      // Parse the AI response into structured data
      return this.parseAIResponse(response);
    } catch (error) {
      console.error('AI research failed:', error);
      throw error;
    }
  }

  /**
   * Parse AI response into structured enrichment data
   */
  private parseAIResponse(response: string): CompanyEnrichmentData {
    console.log('AI Response to parse:', response);
    
    try {
      // Try to extract JSON if the response contains it
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        console.log('Parsed JSON from AI response:', parsed);
        return {
          ...parsed,
          lastEnriched: new Date(),
          confidence: 'high'
        };
      }

      // Fallback: Parse text response into structured data
      console.log('Falling back to text parsing...');
      const enrichmentData: CompanyEnrichmentData = {
        description: this.extractSection(response, ['description', 'about', 'overview']) || response.substring(0, 500),
        industry: this.extractSection(response, ['industry', 'sector', 'market']),
        founded: this.extractSection(response, ['founded', 'established', 'year']),
        headquarters: this.extractSection(response, ['headquarters', 'hq', 'location']),
        website: this.extractSection(response, ['website', 'url', 'site']),
        employees: this.extractSection(response, ['employees', 'staff', 'team size']),
        revenue: this.extractSection(response, ['revenue', 'sales', 'turnover']),
        businessModel: this.extractSection(response, ['business model', 'model', 'approach']),
        targetMarket: this.extractSection(response, ['target', 'market', 'customers']),
        fundingInfo: this.extractSection(response, ['funding', 'investment', 'capital']),
        lastEnriched: new Date(),
        confidence: 'medium'
      };
      console.log('Parsed enrichment data from text:', enrichmentData);

      // Extract arrays
      enrichmentData.products = this.extractList(response, ['products', 'services', 'offerings']);
      enrichmentData.competitors = this.extractList(response, ['competitors', 'competition', 'rivals']);
      enrichmentData.tags = this.generateTags(enrichmentData);

      return enrichmentData;
    } catch (error) {
      console.error('Error parsing AI response:', error);
      return {
        description: response.substring(0, 500),
        confidence: 'low',
        lastEnriched: new Date()
      };
    }
  }

  /**
   * Extract a section from text response
   */
  private extractSection(text: string, keywords: string[]): string | undefined {
    for (const keyword of keywords) {
      const regex = new RegExp(`${keyword}[:\s]+([^.\n]+)`, 'i');
      const match = text.match(regex);
      if (match) {
        return match[1].trim();
      }
    }
    return undefined;
  }

  /**
   * Extract a list from text response
   */
  private extractList(text: string, keywords: string[]): string[] {
    for (const keyword of keywords) {
      const regex = new RegExp(`${keyword}[:\s]+([^\n]+)`, 'i');
      const match = text.match(regex);
      if (match) {
        // Split by common delimiters
        return match[1]
          .split(/[,;]/)
          .map(item => item.trim())
          .filter(item => item.length > 0);
      }
    }
    return [];
  }

  /**
   * Generate relevant tags based on enrichment data
   */
  private generateTags(data: CompanyEnrichmentData): string[] {
    const tags: string[] = [];

    // Add industry tags
    if (data.industry) {
      tags.push(data.industry.toLowerCase());
    }

    // Add size tags
    if (data.employees) {
      const employeeCount = parseInt(data.employees.replace(/\D/g, ''));
      if (employeeCount < 50) tags.push('startup');
      else if (employeeCount < 200) tags.push('small business');
      else if (employeeCount < 1000) tags.push('medium business');
      else tags.push('enterprise');
    }

    // Add product/service tags
    if (data.products) {
      data.products.slice(0, 3).forEach(product => {
        tags.push(product.toLowerCase());
      });
    }

    return [...new Set(tags)]; // Remove duplicates
  }

  /**
   * Search for companies using AI
   */
  async searchCompanies(searchQuery: string): Promise<Company[]> {
    try {
      const query = `Search for companies matching: "${searchQuery}". 
      Provide company names, industries, and brief descriptions.
      Format as a list.`;

      const systemPrompt = `You are a business research assistant. 
      Provide accurate company information based on your knowledge.`;

      const response = await openaiService.generalQuery(query, systemPrompt);

      // Parse search results
      return this.parseSearchResults(response);
    } catch (error) {
      console.error('Company search failed:', error);
      return [];
    }
  }

  /**
   * Parse search results into company list
   */
  private parseSearchResults(response: string): Company[] {
    const companies: Company[] = [];
    
    // Simple parsing - split by lines and extract company info
    const lines = response.split('\n');
    lines.forEach(line => {
      const match = line.match(/^[-\d.]\s*(.+?)(?:\s*[-–]\s*(.+))?$/);
      if (match) {
        companies.push({
          id: `search-${Date.now()}-${Math.random()}`,
          name: match[1].trim(),
          industry: match[2]?.trim() || 'Unknown',
          createdAt: new Date(),
          updatedAt: new Date()
        } as Company);
      }
    });

    return companies;
  }

  /**
   * Get competitor analysis
   */
  async analyzeCompetitors(companyName: string): Promise<{
    competitors: string[];
    analysis: string;
  }> {
    const query = `Analyze the competitive landscape for "${companyName}". 
    List main competitors and provide a brief competitive analysis.`;

    const systemPrompt = `You are a competitive analysis expert. 
    Provide accurate market analysis and competitor insights.`;

    const response = await openaiService.generalQuery(query, systemPrompt);

    return {
      competitors: this.extractList(response, ['competitors', 'competition']),
      analysis: response
    };
  }

  /**
   * Clear cache for a specific company or all
   */
  clearCache(companyName?: string): void {
    if (companyName) {
      this.cache.delete(companyName);
    } else {
      this.cache.clear();
    }
  }
}

// Export singleton instance
const companyEnrichmentService = new CompanyEnrichmentService();
export default companyEnrichmentService;
